import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {SettingTextareaComponent} from "../../../../components/Settings/SettingsTextareaComponent"
import {useKeyboard} from "../../../../hooks/keyboard"
import {useLocalization} from "../../../../hooks/localization"
import {useMessage} from "../../../../hooks/message"
import {useRPC} from "../../../../hooks/rpc"
import {onClickHandler} from "../../../../utils/onclick"
import {ProjectInfo, ProjectInfoDetails} from "../../../../store/control/state"
import {useDispatch, useSelector} from "react-redux"
import "./index.scss"
import {setEntityListAction} from "../../../../store/control/actions"
import {changeBusyAction} from "../../../../store/ui/actions"

const BACK_URL = "/settings"
const date = new Date().getTime()

type Props = {
    date: any
    details: string
    personel: string
    onClick: () => void
}

const convertTime = (tm: number) => {
    const t = new Date(tm)
    return `${t.getDate()}/${t.getMonth() + 1}/${t.getFullYear()}`
}

export const ProjectInformationDetails = memo(function ProjectInformationDetails({
                                                                                     date,
                                                                                     details,
                                                                                     personel,
                                                                                     onClick
                                                                                 }: Props) {
    const {t} = useLocalization()
    return (
        <div className="left-project-column">
            <span>{date}</span>
            <span>{personel}</span>
            <span>{details}</span>
            <span>
                <button {...onClickHandler(onClick)}>{t("edit")}</button>
            </span>
        </div>
    )
})

export const ProjectInformation = memo(function ProjectInformation() {
    const {t} = useLocalization()
    const {failure} = useMessage()
    const [rpc] = useRPC()
    const dispatch = useDispatch()
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard("numpad", true)
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] = useKeyboard(
        "numpad-extended",
        true
    )

    const projectInfoList = useSelector(state => state.control.projectInformationList).find(r => r)
    const [projectName, setProjectName] = useState(projectInfoList?.project_name || "")
    const [floor, setFloor] = useState(projectInfoList?.floor_apartment)
    const [projectNumber, setProjectNumber] = useState(projectInfoList?.project_number)
    const [dealerCode, setDealerCode] = useState(projectInfoList?.dealer_no)
    const [country, setCountry] = useState(projectInfoList?.country)
    const [personelName, setPersonelName] = useState("")
    const [projectExplanation, setProjectExplanation] = useState("")
    const [selectedId, setSelectedId] = useState<string>("")
    const [editMode, setEditMode] = useState(false)

    const onBlur = useCallback(() => {
        setActiveInput(null)
        setActiveNumpadInput(null)
        setActiveNumpadExtendedInput(null)
    }, [setActiveInput, setActiveNumpadExtendedInput, setActiveNumpadInput])

    const onClickHandlerInfo = useCallback(
        (id: string) => {
            const findInfo = projectInfoList.project_explanation_list.find(r => r.id === id)
            setSelectedId(id)
            setEditMode(true)
            setPersonelName(findInfo?.technician_name)
            setProjectExplanation(findInfo?.project_explanation)
        },
        [projectInfoList]
    )

    const onCancelPress = useCallback(() => {
        setEditMode(false)
        setPersonelName("")
        setProjectExplanation("")
    }, [])

    const onEditPress = useCallback(() => {
        if (!editMode) {
            return
        }
        const updateInformation: ProjectInfoDetails = {
            id: selectedId,
            technician_name: personelName,
            time_span: date,
            project_explanation: projectExplanation
        }
        dispatch(changeBusyAction(true))
        rpc.updateProjectExplanation(updateInformation)
            .then(() => {
                dispatch(changeBusyAction(false))
                onCancelPress()
            })
            .catch(failure)
            .finally(() => dispatch(changeBusyAction(false)))
    }, [failure, dispatch, onCancelPress, rpc, editMode, selectedId, personelName, projectExplanation])

    const isFieldsValid = useCallback((): boolean => {
        if (projectName == null || projectName.length === 0) {
            failure(t("project_informations.pls_enter_project_name"))

            return false
        }
        if (floor == null || floor.length === 0) {
            failure(t("project_informations.pls_enter_floor_name"))

            return false
        }
        if (projectNumber == null || projectNumber.length === 0) {
            failure(t("project_informations.pls_enter_project_number"))

            return false
        }
        if (dealerCode == null || dealerCode.length === 0) {
            failure(t("project_informations.pls_enter_dealer_number"))

            return false
        }
        if (country == null || country.length === 0) {
            failure(t("project_informations.pls_enter_country"))

            return false
        }

        if (personelName == null || personelName.length === 0) {
            failure(t("project_informations.pls_enter_personel_name"))

            return false
        }

        if (projectExplanation == null || projectExplanation.length === 0) {
            failure(t("project_informations.pls_enter_explanation"))

            return false
        }

        return true
    }, [failure, t, projectName, floor, projectNumber, dealerCode, country, projectExplanation, personelName])

    const onSavePress = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }
        const projectListInfo: ProjectInfo = {
            id: projectInfoList?.id || null,
            project_name: projectName,
            project_number: projectNumber,
            floor_apartment: floor,
            dealer_no: dealerCode,
            country: country,
            technician_name: personelName,
            time_span: date,
            project_explanation: projectExplanation
        }
        dispatch(changeBusyAction(true))
        rpc.updateProjectInformation(projectListInfo)
            .then(() => {
                dispatch(changeBusyAction(false))
                setPersonelName("")
                setProjectExplanation("")
            })
            .catch(failure)
            .finally(() => dispatch(changeBusyAction(false)))
    }, [
        isFieldsValid,
        failure,
        dispatch,
        projectInfoList?.id,
        rpc,
        projectName,
        floor,
        projectNumber,
        dealerCode,
        country,
        projectExplanation,
        personelName
    ])

    const button = useMemo(
        () => [
            {
                text: t("add"),
                className: "green",
                onClick: onSavePress,
                hidden: editMode
            },
            {
                text: t("cancel"),
                className: "red",
                onClick: onCancelPress,
                hidden: !editMode
            },
            {
                text: t("save"),
                className: "green",
                onClick: onEditPress,
                hidden: !editMode
            }
        ],
        [t, onSavePress, onEditPress, onCancelPress, editMode]
    )

    useEffect(() => {
        rpc.getProjectInformationt().then(data => dispatch(setEntityListAction(data, "projectInformation")))
    }, [dispatch, rpc])

    useEffect(() => {
        setDealerCode(projectInfoList?.dealer_no || "")
        setCountry(projectInfoList?.country || "")
        setFloor(projectInfoList?.floor_apartment || "")
        setProjectName(projectInfoList?.project_name || "")
        setProjectNumber(projectInfoList?.project_number || "")
    }, [projectInfoList])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={button}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t("project_informations.enter_project_name")}
                            label={t("project_informations.project_name")}
                            value={projectName}
                            setValue={setProjectName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                            disabled={editMode}
                            readOnly={editMode}
                        />
                        <SettingInputComponent
                            placeholder={t("project_informations.enter_floor")}
                            label={t("project_informations.floor")}
                            value={floor}
                            setValue={setFloor}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                            disabled={editMode}
                            readOnly={editMode}
                        />
                        <SettingInputComponent
                            placeholder={t("project_informations.enter_project_number")}
                            label={t("project_informations.project_number")}
                            value={projectNumber}
                            setValue={setProjectNumber}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                            disabled={editMode}
                            readOnly={editMode}
                        />
                        <SettingInputComponent
                            placeholder={t("project_informations.enter_dealer_number")}
                            label={t("project_informations.dealer_number")}
                            value={dealerCode}
                            setValue={setDealerCode}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                            disabled={editMode}
                            readOnly={editMode}
                        />
                        <SettingInputComponent
                            placeholder={t("project_informations.enter_country")}
                            label={t("project_informations.country")}
                            value={country}
                            setValue={setCountry}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                            disabled={editMode}
                            readOnly={editMode}
                        />

                        <SettingInputComponent
                            placeholder={t("project_informations.enter_personel_name")}
                            label={t("project_informations.personel_name")}
                            value={personelName}
                            setValue={setPersonelName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingTextareaComponent
                            maxLength={250}
                            placeholder={t("project_informations.enter_explanation")}
                            label={t("project_informations.explanation")}
                            value={projectExplanation}
                            setValue={setProjectExplanation}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                    </div>
                    <div className="content-wrapper">
                        <div className="left-project-details-container">
                            <div className="left-project-title">
                                <span>{t("project_informations.date")}</span>
                                <span>{t("project_informations.personel")}</span>
                                <span>{t("project_informations.explanation")}</span>
                            </div>
                            <div className="left-project-details">
                                {projectInfoList?.project_explanation_list?.map(project => (
                                    <ProjectInformationDetails
                                        key={project?.id}
                                        date={convertTime(project?.time_span)}
                                        personel={project?.technician_name}
                                        details={project?.project_explanation}
                                        onClick={() => onClickHandlerInfo(project?.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
            {renderNumpadKeyboard()}
            {renderNumpadExtendedKeyboard()}
        </PasswordContainer>
    )
})
